import { useEffect } from 'react';
import { Drawer, Form, Input, Select, Button, App, Switch } from 'antd';
import JsonValueField, { isJsonLike } from './JsonValueField.js';
import { usePortalCtx } from '../context.js';
import { apiPost, apiPut, apiDelete } from '../api.js';

export type ConfigScope = 'global' | 'site';

export interface ConfigRow {
  key: string;
  value: string;
  updatedAt: string;
  siteKey?: string;
}

type UpsertPayload = {
  key: string;
  value: string;
  scope: ConfigScope;
  siteKey?: string;
};

export default function UpsertConfigDrawer(props: {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: ConfigRow;
  onClose: () => void;
  onSaved: (next: ConfigRow) => void;
  onDeleted?: (id: { key: string; siteKey?: string }) => void;
}) {
  const { open, mode, initial, onClose, onSaved, onDeleted } = props;
  const [form] = Form.useForm<UpsertPayload>();
  const { registryUrl, siteKey: ctxSiteKey } = usePortalCtx();
  const { message, modal } = App.useApp();

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      form.setFieldsValue({
        key: initial.key,
        value: initial.value,
        scope: initial.siteKey ? 'site' : 'global',
        siteKey: initial.siteKey ?? ctxSiteKey,
      });
    } else {
      form.setFieldsValue({
        key: '',
        value: '',
        scope: ctxSiteKey ? 'site' : 'global',
        siteKey: ctxSiteKey,
      });
    }
  }, [open, mode, initial, ctxSiteKey, form]);

  async function submit(values: UpsertPayload) {
    const payload = {
      key: values.key.trim(),
      value: values.value,
      siteKey: values.scope === 'site' ? values.siteKey?.trim() : undefined,
    };

    try {
      const url = new URL('/admin/config', registryUrl);
      const res =
        mode === 'create'
          ? await apiPost<ConfigRow>(url.toString(), payload)
          : await apiPut<ConfigRow>(url.toString(), payload);

      message.success(mode === 'create' ? 'Config added' : 'Config updated');
      onSaved({
        key: res.key,
        value: res.value,
        updatedAt: res.updatedAt ?? new Date().toISOString(),
        siteKey: res.siteKey,
      });
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Save failed');
    }
  }

  async function confirmDelete() {
    if (!initial) return;
    const ok = await (
      modal.confirm({
        title: 'Delete config entry?',
        content: `Delete ${initial.siteKey ? `[${initial.siteKey}] ` : ''}${initial.key}?`,
        okText: 'Delete',
        okButtonProps: { danger: true },
      }) as unknown as Promise<unknown>
    )
      .then(() => true)
      .catch(() => false);

    if (!ok) return;
    try {
      const url = new URL('/admin/config', registryUrl);
      url.searchParams.set('key', initial.key);
      if (initial.siteKey) url.searchParams.set('siteKey', initial.siteKey);
      await apiDelete(url.toString());
      message.success('Config deleted');
      onDeleted?.({ key: initial.key, siteKey: initial.siteKey });
      onClose();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  const isEdit = mode === 'edit';
  const disableKey = isEdit;

  return (
    <Drawer
      title={isEdit ? 'Edit Config' : 'Add Config'}
      width={560}
      open={open}
      onClose={onClose}
      destroyOnClose
      maskClosable={!form.isFieldsTouched()}
      extra={
        isEdit ? (
          <Button danger onClick={confirmDelete}>
            Delete
          </Button>
        ) : null
      }
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          label="Key"
          name="key"
          rules={[
            { required: true, message: 'Enter a key' },
            { min: 2, message: 'At least 2 characters' },
          ]}
        >
          <Input placeholder="e.g. consent.banner.enabled" disabled={disableKey} />
        </Form.Item>

        <Form.Item
          label="Value"
          name="value"
          rules={[
            { required: true, message: 'Enter a value' },
            {
              validator: async (_, v: string) => {
                if (typeof v === 'string' && isJsonLike(v)) {
                  JSON.parse(v);
                }
              },
            },
          ]}
        >
          <JsonValueField
            value={Form.useWatch('value', form) ?? ''}
            onChange={(next) => form.setFieldValue('value', next)}
          />
        </Form.Item>

        <Form.Item label="Scope" name="scope">
          <Select
            options={[
              { label: 'Global', value: 'global' },
              { label: 'Site', value: 'site' },
            ]}
            onChange={(v: ConfigScope) => {
              if (v === 'global') form.setFieldsValue({ siteKey: undefined });
              if (v === 'site' && !form.getFieldValue('siteKey'))
                form.setFieldsValue({ siteKey: ctxSiteKey });
            }}
          />
        </Form.Item>

        <Form.Item
          label="Site key"
          name="siteKey"
          dependencies={['scope']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, v) {
                if (getFieldValue('scope') === 'site' && !v)
                  return Promise.reject(new Error('Site key required for site scope'));
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="e.g. howth-yc" disabled={form.getFieldValue('scope') === 'global'} />
        </Form.Item>

        <Form.Item style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit">
            {isEdit ? 'Save' : 'Create'}
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={onClose}>
            Cancel
          </Button>
        </Form.Item>

        <Form.Item label="Tips">
          <Switch checked disabled />{' '}
          <span style={{ marginLeft: 8 }}>Config changes take effect immediately.</span>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
